import { canonical, integer } from '../kernel/primitives.ts';
import { assertCombatAction, type CombatAction, type CombatState } from '../combat/index.ts';
import { TacticalPcCommandSchema, type TacticalPcCommand } from '@dissidium/contracts';
import { validateTacticalOrder } from '../combat/tactics.ts';

export type PcCommand =
  | TacticalPcCommand
  | Readonly<{ type: 'pause'; paused: boolean }>
  | Readonly<{ type: 'queue'; action: CombatAction }>
  | Readonly<{ type: 'clear-queue' }>;
export type AcceptedPcIntent = Readonly<{ tick: number; sequence: number; command: PcCommand }>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;
const hasKeys = (value: object, keys: readonly string[]): boolean =>
  Object.keys(value).sort().join(',') === [...keys].sort().join(',');

function parseIntent(raw: unknown): AcceptedPcIntent {
  if (!isRecord(raw) || !hasKeys(raw, ['tick', 'sequence', 'command']) ||
      !Number.isSafeInteger(raw.tick) || Number(raw.tick) < 0 ||
      !Number.isSafeInteger(raw.sequence) || Number(raw.sequence) < 0 || !isRecord(raw.command)) throw new Error('Invalid ticked PC intent');
  const command = raw.command;
  if (command.type === 'pause') {
    if (!hasKeys(command, ['type', 'paused']) || typeof command.paused !== 'boolean') throw new Error('Pause intents contain only a boolean state');
  } else if (command.type === 'queue') {
    if (!hasKeys(command, ['type', 'action'])) throw new Error('Queue intents contain only one PC action');
    assertCombatAction(command.action);
  } else if (command.type === 'clear-queue') {
    if (!hasKeys(command, ['type'])) throw new Error('Clear-queue intents contain no target actor');
  } else if (command.type === 'act' || command.type === 'danger-options') {
    TacticalPcCommandSchema.parse(command);
  } else throw new Error('Unknown PC intent');
  return raw as unknown as AcceptedPcIntent;
}

export function applyPcIntent(input: CombatState, raw: unknown): CombatState {
  const intent = parseIntent(raw);
  integer(intent.tick);
  integer(intent.sequence);
  const pc = input.actors[input.pcId];
  if (!pc || pc.status === 'dead') throw new Error('A dead PC cannot issue actions');
  if (intent.tick !== input.tick || intent.sequence !== input.lastPcSequence + 1) throw new Error('PC intents require the current simulation tick and next sequence');
  const state = structuredClone(input);
  if (intent.command.type === 'pause') state.paused = intent.command.paused;
  else if (intent.command.type === 'queue') state.pcQueue.push(structuredClone(intent.command.action));
  else if (intent.command.type === 'clear-queue') state.pcQueue = [];
  else {
    if (!state.tactics) throw new Error('Tactical commands require combat setup');
    if (intent.command.type === 'danger-options') state.tactics.dangerOptions = [...new Set(intent.command.triggers)].sort();
    else {
      const action = intent.command.action;
      validateTacticalOrder(state, state.pcId, action);
      const own = state.tactics.actors[state.pcId]!;
      // Treat, Brace, Lunge and Withdraw answer a telegraph, so choosing one breaks off
      // your own unresolved wind-up. Other orders wait for it. Recovery always binds,
      // so re-clicking can never speed a strike.
      const underway = own.commitment?.action;
      if (underway && ['consume', 'brace', 'lunge', 'withdraw'].includes(action.type) && canonical(underway) !== canonical(action) && own.commitment!.resolvesTick > state.tick) {
        state.events.push({ type: 'action-ended', tick: state.tick, actorId: state.pcId, action: structuredClone(underway), outcome: 'interrupted', reason: 'Broken off for a new order.' });
        own.commitment = null;
        own.readyTick = state.tick;
      }
      own.order = structuredClone(action);
      state.pcQueue = [];
    }
  }
  state.lastPcSequence = intent.sequence;
  state.acceptedPcIntents.push(structuredClone(intent));
  return state;
}
